import { Loader2, ChevronLeft, ChevronRight, Search, FileText, Mail, MailX } from "lucide-react";
import { EUR } from "./states";

function EmailSentBadge({ row }) {
  // PR (Pagamento Recebido) é sempre auto-enviado regardless do flag —
  // não dependemos do `state_email_sent` que pode ter sido 'N' do admin.
  const isPaid = row.state_id === "PR";
  const enviado = isPaid || row.state_email_sent === "S";
  if (enviado) {
    return (
      <span data-testid={`email-sent-${row.ID_Encomenda}`}
        title={isPaid ? "Pagamento Recebido — email automático ao cliente" : "Email enviado ao cliente neste estado"}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700">
        <Mail size={12} /> Sim
      </span>
    );
  }
  return (
    <span data-testid={`email-not-sent-${row.ID_Encomenda}`}
      title="Admin não marcou enviar email para esta transição"
      className="inline-flex items-center gap-1 text-[11px] text-[#6B7280]">
      <MailX size={12} /> Não
    </span>
  );
}

function OrderRow({ row, onTransition, onBilling }) {
  return (
    <tr data-testid={`order-row-${row.ID_Encomenda}`} className="border-b border-[#F3F0E7] hover:bg-[#FAF8F2]">
      <td className="px-3 py-2 font-mono text-[#0F1829]">
        <a href={`/encomendas/${row.ID_Encomenda}`} target="_blank" rel="noreferrer" className="hover:underline">#{row.ID_Encomenda}</a>
      </td>
      <td className="px-3 py-2">
        <div className="font-medium" data-testid={`order-client-${row.ID_Encomenda}`}>
          {row.NomeCompleto || '—'}{row.ID_Cliente ? <span className="text-[#978D78] font-normal"> ({row.ID_Cliente})</span> : null}
        </div>
        <div className="text-[11px] text-[#6B7280]">{row.Email || ''}</div>
      </td>
      <td className="px-3 py-2 font-mono text-xs text-[#4B5563]">{(row.DataEncomenda || '').slice(0, 16)}</td>
      <td className="px-3 py-2 font-mono text-xs text-[#4B5563]">{(row.state_since || '').slice(0, 16)}</td>
      <td className="px-3 py-2"><EmailSentBadge row={row} /></td>
      <td className="px-3 py-2 font-mono text-right font-semibold text-[#0F1829] whitespace-nowrap">{EUR(row.PrecoFinal)}</td>
      <td className="px-3 py-2 text-right">
        <button onClick={() => onTransition(row)} data-testid={`transition-btn-${row.ID_Encomenda}`}
          className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-[#E6E1D6] bg-[#FAF8F2] hover:bg-[#0F1829] hover:text-white hover:border-[#0F1829] transition">
          Alterar estado →
        </button>
        <button onClick={() => onBilling(row)} data-testid={`billing-btn-${row.ID_Encomenda}`}
          className="ml-2 inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-[#E6E1D6] bg-white hover:bg-[#F5F2EB] transition" title="Faturação">
          <FileText size={12} /> Faturação
        </button>
      </td>
    </tr>
  );
}

const HEAD_CLS = "px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]";

export default function OrdersTable({
  rows, loading, error,
  total, page, setPage, totalPages,
  pageSize, setPageSize, search, setSearch,
  onTransition, onBilling,
}) {
  return (
    <>
      <div className="flex items-center justify-end gap-2 mb-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#978D78]" />
          <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="ID, email ou nome..." data-testid="search-input"
            className="pl-8 pr-3 py-1.5 text-sm border border-[#E6E1D6] rounded w-72 outline-none focus:border-[#0F1829]" />
        </div>
        <select value={pageSize} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}
          className="px-2 py-1.5 text-sm border border-[#E6E1D6] rounded">
          <option value={12}>12/pg</option><option value={25}>25/pg</option><option value={50}>50/pg</option>
        </select>
      </div>

      {error && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded">{error}</div>}

      <div className="bg-white border border-[#E6E1D6] rounded-md overflow-x-auto max-w-full">
        <table className="text-sm" style={{ minWidth: "100%", width: "max-content" }}>
          <thead className="bg-[#F5F2EB] border-b border-[#E6E1D6]">
            <tr>
              <th className={`${HEAD_CLS} text-left`}>ID</th>
              <th className={`${HEAD_CLS} text-left`}>Cliente</th>
              <th className={`${HEAD_CLS} text-left`}>Data</th>
              <th className={`${HEAD_CLS} text-left`}>Desde</th>
              <th className={`${HEAD_CLS} text-left`} title="Email enviado ao cliente para o estado actual">Email?</th>
              <th className={`${HEAD_CLS} text-right`}>Preço</th>
              <th className={`${HEAD_CLS} text-right`}>Acção</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-[#6B7280]"><Loader2 size={16} className="inline animate-spin mr-1" /> A carregar...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#6B7280]">Sem encomendas.</td></tr>}
            {!loading && rows.map((r) => (
              <OrderRow key={r.ID_Encomenda} row={r}
                onTransition={onTransition} onBilling={onBilling} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-center gap-2 mt-3 text-sm">
        <span className="text-xs text-[#6B7280] mr-2">{total.toLocaleString("pt-PT")} encomendas</span>
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
          className="p-1.5 rounded border border-[#E6E1D6] disabled:opacity-30 hover:bg-[#F5F2EB]"><ChevronLeft size={14} /></button>
        <span className="text-xs text-[#6B7280]">Pág {page} / {totalPages}</span>
        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
          className="p-1.5 rounded border border-[#E6E1D6] disabled:opacity-30 hover:bg-[#F5F2EB]"><ChevronRight size={14} /></button>
      </div>
    </>
  );
}
